import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';

import type { ContractMetric, RevenueContributionData } from '../../types/report';

interface ContractChartProps {
  metrics: ContractMetric[];
  revenueData: RevenueContributionData[];
  avgRevenue: {
    newContract: number;
    existingContract: number;
  };
}

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444'];

const formatCurrency = (value: number) => {
  if (value >= 100000000) {
    return `${(value / 100000000).toFixed(2)}억`;
  }
  if (value >= 10000) {
    return `${(value / 10000).toFixed(0)}만`;
  }
  return value.toLocaleString();
};

export const ContractChart = ({ metrics, revenueData, avgRevenue }: ContractChartProps) => {
  const pieData = [
    {
      name: '기존 계약',
      value: metrics.find((m) => m.key === 'existingContracts')?.value || 0,
    },
    {
      name: '신규 계약',
      value: metrics.find((m) => m.key === 'newContracts')?.value || 0,
    },
  ];

  const newContractBreakdown = [
    {
      name: '유료 신규',
      value: metrics.find((m) => m.key === 'paidNewContracts')?.value || 0,
    },
    {
      name: '무료 신규',
      value: metrics.find((m) => m.key === 'freeNewContracts')?.value || 0,
    },
  ];

  const avgRevenueData = [
    { name: '신규 계약', value: avgRevenue.newContract },
    { name: '기존 계약', value: avgRevenue.existingContract },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h4 className="mb-4 text-sm font-medium text-gray-700">계약 구성</h4>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value.toLocaleString()}건`}
                >
                  <Cell fill="#3b82f6" />
                  <Cell fill="#10b981" />
                </Pie>
                <Tooltip formatter={(value) => `${Number(value).toLocaleString()}건`} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div>
          <h4 className="mb-4 text-sm font-medium text-gray-700">신규 계약 내역</h4>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={newContractBreakdown}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}건`}
                >
                  <Cell fill="#10b981" />
                  <Cell fill="#f59e0b" />
                </Pie>
                <Tooltip formatter={(value) => `${Number(value).toLocaleString()}건`} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div>
        <h4 className="mb-4 text-sm font-medium text-gray-700">건당 평균 매출 비교</h4>
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={avgRevenueData} layout="vertical" margin={{ top: 20, right: 30, left: 80, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis type="number" tickFormatter={(v) => `${(v / 10000).toFixed(0)}만`} tick={{ fontSize: 12 }} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={70} />
              <Tooltip formatter={(value) => `${Number(value).toLocaleString()}원`} />
              <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                <Cell fill="#10b981" />
                <Cell fill="#3b82f6" />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          신규 계약 건당 평균 {avgRevenue.newContract.toLocaleString()}원은 기존 계약 {avgRevenue.existingContract.toLocaleString()}원 대비
          약 {((avgRevenue.newContract / avgRevenue.existingContract) * 100).toFixed(1)}% 수준
        </p>
      </div>
    </div>
  );
};
